import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import SearchBar from "@/components/business/SearchBar";
import KnowledgeResultCard from "@/components/business/KnowledgeResultCard";
import RecommendationCard from "@/components/business/RecommendationCard";

export function Knowledge() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(null);

  useEffect(() => {
    api.get(cat ? `/knowledge?category=${cat}` : "/knowledge").then(({ data }) => setItems(data));
  }, [cat]);

  const filtered = items.filter(a =>
    !q || a.title.toLowerCase().includes(q.toLowerCase()) || a.excerpt.toLowerCase().includes(q.toLowerCase()) || (a.tags || []).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">Knowledge Center</div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-2">Tax guides, demystified.</h1>
          <p className="mt-3 text-muted-foreground">Practical articles, calculators and Budget breakdowns — written by Chartered Accountants.</p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <SearchBar value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles, sections, tags…" className="flex-1 max-w-md" inputClassName="h-11" data-testid="kb-search" />
          <div className="flex gap-1.5 overflow-x-auto">
            {[{ k: null, l: "All" }, { k: "guide", l: "Guides" }, { k: "news", l: "News" }, { k: "faq", l: "FAQ" }, { k: "calculator", l: "Calculators" }].map(x => (
              <Button key={x.l} size="sm" variant={cat === x.k ? "default" : "outline"} onClick={() => setCat(x.k)} data-testid={`kb-cat-${x.l.toLowerCase()}`}>{x.l}</Button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="articles-grid">
          {filtered.map((a) => (
            <KnowledgeResultCard key={a.slug} article={a} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get(`/knowledge/${slug}`).then(({ data }) => setA(data)).catch(() => {});
  }, [slug]);

  if (!a) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12">
        <Button asChild variant="ghost" size="sm" className="mb-6"><Link to="/knowledge">← Back to Knowledge</Link></Button>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{a.category}</Badge>
        <h1 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.1] text-balance">{a.title}</h1>
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
          {a.author} · {a.read_time} min read
        </div>
        {a.cover && <img src={a.cover} alt={a.title} className="mt-8 w-full h-72 object-cover rounded-xl border border-border" />}
        <article className="mt-8 prose dark:prose-invert max-w-none">
          {a.body.split("\n\n").map((p, i) => {
            if (p.startsWith("## ")) return <h2 key={i} className="font-display text-2xl font-bold mt-8 mb-3">{p.replace("## ", "")}</h2>;
            return <p key={i} className="text-base leading-relaxed text-muted-foreground mb-4 whitespace-pre-line">{p}</p>;
          })}
        </article>

        <div className="mt-12">
          <RecommendationCard
            title="Apply this to your return"
            description="Open the filing wizard and we'll guide you through the relevant section."
            actionLabel="Start filing"
            actionHref="/dashboard"
            actionIcon={ArrowRight}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
